import { React } from 'Imports';
import { ConfigService } from '$State/ConfigFreezerService';
import SingleSelectDropdownMenu from '$Components/Shared/Dropdown/SingleSelectDropdownMenu';
import { useEffect, useState } from 'react';
import { StyledPlainSubmitButton } from '$Components/VideoEvents/VideoEventList/VideoEventListStyles';
import { FleetSettingsService, IFleetSettingsServiceInjectedProps } from '$State/FleetSettingsFreezerService';
import { VideoEventRetentionSettingsResponse } from '$Generated/api';
import { VideoRetentionSaveDialog } from './VideoRetentionSaveDialog';
import { AlertMessage } from '$Components/Shared/AlertMessage';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import { isEncompass } from '$Utilities/integrationPlatform';
import { Box } from '@mui/material';

const styles = require('./VideoRetention.scss') as {
    videoRetentionDescriptionGeotab: string;
    videoRetentionDescriptionEncompass: string;
    dropdownRetentionDays: string;
    dropdownPurge: string;
    retentionRow: string;
};
interface IVideoRetentionProps {
    onDirtyStateChange: (isDirty: boolean) => void;
    resetRetentionData?: number;
}

type VideoRetentionProps = IVideoRetentionProps & IFleetSettingsServiceInjectedProps;

const DEFAULT_VALUES = {
    retentionDays: 180,
    keepEventData: true,
} as const;

const _VideoRetention = (props: VideoRetentionProps) => {
    // dropdowns
    const [RetentionDaysDropdownIsOpen, setRetentionDaysDropdownIsOpen] = useState(false);
    const [purgeDropdownIsOpen, setpurgeDropdownIsOpen] = useState(false);
    const [retentionDaysValue, setRetentionDaysValue] = useState(180);
    const [keepDataValue, setKeepDataValue] = useState(true);
    const [disableDropdown, setDisableDropdown] = useState(false);

    // submit state controls
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarType, setSnackbarType] = useState(AlertTypes.Success);

    // existing retention settings state
    const [retentionSettingsState, setRetentionSettingsState] = useState<VideoEventRetentionSettingsResponse>({
        retentionDays: DEFAULT_VALUES.retentionDays,
        keepEventData: DEFAULT_VALUES.keepEventData,
    });

    // snackbar messages
    const saveSuccessfulMessage = 'Settings saved successfully.';
    const saveFailedMessage = 'Settings unable to be saved.';

    // generate retention days item list
    const generateRetentionDaysItemList = (days: number[]) => {
        return days.map((day) => ({
            label: day == 1095 ? '3 years' : `${day} days`,
            value: day,
            onClick: () => {
                handleRetentionDayChange(day);
            },
        }));
    };

    const handleRetentionDayChange = (day: number) => {
        setRetentionDaysValue(day);
    };

    // check to see if the form has changed
    const hasUnsavedChanges = (keepData: boolean, retentionDays: number) => {
        return retentionSettingsState.keepEventData != keepData || retentionSettingsState.retentionDays != retentionDays;
    };

    const [retentionDaysItemList, setRetentionDaysItemList] = useState(generateRetentionDaysItemList([30, 60, 90, 180]));

    const integrationPlatformStyle = ConfigService.isIntegrationPlatformEncompass()
        ? styles.videoRetentionDescriptionEncompass
        : styles.videoRetentionDescriptionGeotab;

    const fetchData = async () => {
        await props.FleetSettings.getVideoEventRetentionSettings();
        const fleetState = props.FleetSettings.getState();
        const { getVideoEventRetentionSettings } = fleetState;

        if (getVideoEventRetentionSettings.data != null && getVideoEventRetentionSettings.data != undefined) {
            setRetentionSettingsState(getVideoEventRetentionSettings.data);
        }
        let retentionDaysFromState =
            getVideoEventRetentionSettings.data?.retentionDays == undefined || getVideoEventRetentionSettings.data?.retentionDays == 0
                ? DEFAULT_VALUES.retentionDays
                : getVideoEventRetentionSettings.data?.retentionDays;
        let keepDataState =
            getVideoEventRetentionSettings.data?.retentionDays == 0
                ? DEFAULT_VALUES.keepEventData
                : (getVideoEventRetentionSettings.data?.keepEventData ?? true);
        const availableRetentionValues = getVideoEventRetentionSettings.data?.availableRetentionValues ?? [1095];
        // if there is only one retention value, disable the dropdown and set keepData to true. This means it's a VRS fleet
        if (availableRetentionValues.length == 1) {
            setDisableDropdown(true);
            retentionDaysFromState = availableRetentionValues[0];
            keepDataState = true;
        }

        setRetentionDaysItemList(generateRetentionDaysItemList(availableRetentionValues));

        setRetentionDaysValue(retentionDaysFromState);
        setKeepDataValue(keepDataState);
    };

    //initialize data on first load and on reset signal changes
    useEffect(() => {
        fetchData();
    }, [props.resetRetentionData]);

    // check for changes and fire off a dirty state change
    useEffect(() => {
        const isDirty = hasUnsavedChanges(keepDataValue, retentionDaysValue);
        props.onDirtyStateChange(isDirty);
    }, [keepDataValue, retentionDaysValue, retentionSettingsState]);

    const handleSaveClick = async () => {
        setDisableSubmit(true);
        await props.FleetSettings.saveVideoEventRetentionSettings(retentionDaysValue, keepDataValue);
        const fleetState = props.FleetSettings.getState();
        const { saveVideoEventRetentionSettings } = fleetState;
        if (saveVideoEventRetentionSettings.error) {
            setSnackbarMessage(saveFailedMessage);
            setSnackbarType(AlertTypes.Fail);
        } else {
            setRetentionSettingsState({ keepEventData: keepDataValue, retentionDays: retentionDaysValue });
            setSnackbarMessage(saveSuccessfulMessage);
            setSnackbarType(AlertTypes.Success);
        }

        setDisableSubmit(false);
        setConfirmDialogIsOpen(false);
        setShowSnackbar(true);
    };

    return (
        <div>
            <div className={integrationPlatformStyle}>
                <div className={styles.retentionRow}>
                    <span>Video events automatically be deleted after</span>
                    <div className={styles.dropdownRetentionDays}>
                        <SingleSelectDropdownMenu
                            onClose={() => setRetentionDaysDropdownIsOpen(false)}
                            onOpen={() => setRetentionDaysDropdownIsOpen(true)}
                            items={retentionDaysItemList}
                            open={RetentionDaysDropdownIsOpen}
                            searchProps={undefined}
                            required={true}
                            defaultValue={retentionDaysValue}
                            disabled={disableDropdown}
                            sortItems={false}
                        />
                    </div>
                </div>
                <div className={styles.retentionRow}>
                    <span>
                        Keep historical event data, such as driver scores, for reporting purposes when videos are deleted after the
                        retention period
                    </span>
                    <div className={styles.dropdownPurge}>
                        <SingleSelectDropdownMenu
                            onClose={() => setpurgeDropdownIsOpen(false)}
                            onOpen={() => setpurgeDropdownIsOpen(true)}
                            items={[
                                {
                                    label: 'Yes',
                                    value: 3,
                                    onClick: () => {
                                        setKeepDataValue(true);
                                    },
                                },
                                {
                                    label: 'No',
                                    value: 1,
                                    onClick: () => {
                                        setKeepDataValue(false);
                                    },
                                },
                            ]}
                            open={purgeDropdownIsOpen}
                            searchProps={undefined}
                            required={true}
                            defaultValue={keepDataValue ? 3 : 1}
                            disabled={disableDropdown}
                        />
                    </div>
                </div>
                <StyledPlainSubmitButton
                    onClick={() => setConfirmDialogIsOpen(true)}
                    disabled={disableSubmit || !hasUnsavedChanges(keepDataValue, retentionDaysValue)}
                >
                    Save Changes
                </StyledPlainSubmitButton>
            </div>
            {/* this div is to push the alert to the bottom of the retention dialog */}
            <Box
                sx={{
                    top: '0px',
                    position: 'fixed',
                }}
            >
                <AlertMessage
                    alertType={snackbarType}
                    autoHide={true}
                    openSnackbar={showSnackbar}
                    handleSnackClose={() => setShowSnackbar(false)}
                    message={snackbarMessage}
                    handleSubmit={() => setShowSnackbar(false)}
                    isUndo={true}
                    showIcon={isEncompass}
                    useNewIcons={isEncompass}
                />
            </Box>

            <VideoRetentionSaveDialog
                isOpen={confirmDialogIsOpen}
                retentionDays={retentionDaysValue}
                handleSubmit={handleSaveClick}
                handleModalClosed={() => setConfirmDialogIsOpen(false)}
            />
        </div>
    );
};
export const VideoRetention = ConfigService.inject(FleetSettingsService.inject(_VideoRetention));
